import { useState, useCallback, useEffect, useRef } from 'react';
import { FiVideo } from 'react-icons/fi';
import VideoTreeSidebar from './VideoTreeSidebar';
import { getVideos, getVideoFilterTree, getVideoFilterTreeByCategory } from '../api/publicApi';
import { fetchVideoByIdDirect, fetchVideoById } from '../services/videoService';
import { VideoPreview } from './VideoPlayers';
import { SkeletonBox } from './Skeleton';

/*
const SEARCH_TYPES = [
  { label: 'Par date', value: 'date' },
  { label: 'Par thème', value: 'theme' },
];
*/

function fmtDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/*
function getISOWeek(date) {
  const tempDate = new Date(date.valueOf());
  const dayNum = (date.getDay() + 6) % 7;
  tempDate.setDate(tempDate.getDate() - dayNum + 3);
  const firstThursday = tempDate.valueOf();
  tempDate.setMonth(0, 1);
  if (tempDate.getDay() !== 4) {
    tempDate.setMonth(0, 1 + ((4 - tempDate.getDay() + 7) % 7));
  }
  return 1 + Math.ceil((firstThursday - tempDate) / 604800000);
}

function getWeeksForMonth(year, month) {
  const weeks = new Set();
  const numDays = new Date(year, month, 0).getDate();
  for (let day = 1; day <= numDays; day++) {
    const date = new Date(year, month - 1, day);
    weeks.add(getISOWeek(date));
  }
  return Array.from(weeks).sort((a, b) => a - b);
}
*/

/**
 * Generic video section page.
 *
 * Props:
 *  - section {string}  – backend section enum value (e.g. 'enseignements')
 *  - sidebarLabel {string} – label shown in the sidebar section header
 *  - renderKey {'byYear'|'byTheme'} – tree render mode (default: 'byYear')
 *  - videoTitlePrefix {string|null} – optional text prefix before the date in the player title
 *                                     (null means just show the date)
 *  - showThemeFilter {boolean} – whether to show the theme filter (default: false)
 *  - extraSections {Array|null} – override the default sidebar sections array
 */
export default function VideoSectionPage({
  section,
  sidebarLabel,
  renderKey = 'byYear',
  videoTitlePrefix = null,
  showThemeFilter = false,
  multipleVideosPerDay = false,
  extraSections = null,
}) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  // Start in grid mode so all loaded videos are shown immediately
  const [showGrid, setShowGrid] = useState(true);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [filters, setFilters] = useState({ type: '', annee: '', mois: '', semaine: '', theme: '', dateFrom: '', dateTo: '' });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filterTree, setFilterTree] = useState({ years: [], themes: [], sections: [] });
  const [treeLoading, setTreeLoading] = useState(true);
  const [filterRunId, setFilterRunId] = useState(0);
  const [page, setPage] = useState(1);
  const [allVideos, setAllVideos] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [loading, setLoading] = useState(false);
  // Track if this is a day-based structure (motivation_lundi)
  const isDayBased = section === 'motivation_lundi';
  const limit = 16;
  const filterKeyRef = useRef('');

  useEffect(() => {
    const fetchTree = async () => {
      setTreeLoading(true);
      try {
        const sectionRes = await getVideoFilterTreeByCategory(section);
        let treeData = sectionRes?.data?.data || { years: [], themes: [], sections: [] };
        setFilterTree(treeData);
      } catch (error) {
        console.error('Error fetching video tree:', error);
        setFilterTree({ years: [], themes: [], sections: [] });
      } finally {
        setTreeLoading(false);
      }
    };
    fetchTree();
  }, [section]);

  useEffect(() => {
    const filterKey = JSON.stringify({ section, appliedFilters });
    const isNewFilter = filterKey !== filterKeyRef.current;

    let cancelled = false;
    setLoading(true);

    const params = {
      section,
      statut: 'actif',
      limit,
      page,
      ...(appliedFilters.annee && { year: appliedFilters.annee }),
      ...(appliedFilters.mois && { month: appliedFilters.mois }),
      ...(appliedFilters.semaine && { week: appliedFilters.semaine }),
      ...(appliedFilters.jour && { day: appliedFilters.jour }),
      ...(appliedFilters.theme && { theme: appliedFilters.theme }),
      ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
      ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
    };

    getVideos(params).then(res => {
      if (cancelled) return;
      const incoming = res.data?.data || [];
      const pagination = res.data?.pagination || {};
      if (isNewFilter) {
        filterKeyRef.current = filterKey;
        setAllVideos(incoming);
        
        // Auto-select first video from results if this is a new filter
        if (incoming.length > 0 && isNewFilter) {
          const firstVideo = incoming[0];
          // Auto-fetch full details for first video using direct API
          (async () => {
            try {
              setVideoLoading(true);
              setShowGrid(false);
              const videoId = firstVideo.id || firstVideo._id;
              const fullVideo = await fetchVideoByIdDirect(videoId);
              setSelectedVideo(fullVideo);
              setVideoLoading(false);
            } catch (directError) {
              try {
                const fullVideo = await fetchVideoById(firstVideo.id || firstVideo._id, {
                  section: section,
                  statut: 'actif',
                  limit: 100,
                  page: 1,
                });
                setSelectedVideo(fullVideo);
                setVideoLoading(false);
              } catch (fallbackError) {
                setSelectedVideo(firstVideo);
                setVideoLoading(false);
              }
            }
          })();
        }
      } else {
        setAllVideos(prev => [...prev, ...incoming]);
      }
      setPaginationData(pagination);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, section, filterRunId]);

  // Only play a video when the user explicitly selects one
  const currentVideo = selectedVideo || null;

  const sections = extraSections ?? [{ key: `by_${section}`, label: sidebarLabel, renderKey }];

  /*
  const handleSearch = useCallback(() => {
    setAppliedFilters({ ...filters });
    setPage(1);
    setShowGrid(true);
    setSelectedVideo(null);
    setFilterOpen(false);
  }, [filters]);
  */

  const handleClear = useCallback(() => {
    const emptyFilters = { type: '', annee: '', mois: '', semaine: '', theme: '', dateFrom: '', dateTo: '' };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    filterKeyRef.current = '';
    setFilterRunId(prev => prev + 1);
    setAllVideos([]);
    setPage(1);
    setShowGrid(true);
    setShowAllVideos(false);
    setSelectedVideo(null);
  }, []);

  const hasActiveFilters = Object.values(filters).some(val => val !== '');

  // Only play a video when the user explicitly selects one
  const handleSelect = async (id, video) => {
    try {
      // Build filter params from current appliedFilters
      const filterParams = {
        section: section,
        statut: 'actif',
        limit: 100,
        page: 1,
      };
      
      // Add year/month filters if available
      if (appliedFilters.annee) {
        filterParams.year = appliedFilters.annee;
      }
      if (appliedFilters.mois) {
        filterParams.month = appliedFilters.mois;
      }
      
     
      // Try to fetch full video details from API using the list endpoint
      const fullVideo = await fetchVideoById(id, filterParams);
      setSelectedVideo(fullVideo);
      setShowGrid(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      // Fallback to cached data from allVideos or provided video
      const cachedVideo = allVideos.find(v => v.id === id || v._id === id) || video;
      setSelectedVideo(cachedVideo);
      setShowGrid(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSidebarFilter = async (newFilters) => {
  
    const { _video, _videoId, ...filterOnly } = newFilters;

    // Check if this is a video selection
    if (_video || _videoId) {
      
      // Set loading state immediately
      setVideoLoading(true);
      setShowGrid(false);
      
      try {
        // Determine the video ID to use
        const videoId = _videoId || _video?.id || _video?._id;
        // Try the direct endpoint first
        const fullVideo = await fetchVideoByIdDirect(videoId); 
        setSelectedVideo(fullVideo);
        setVideoLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      } catch (directError) {
        try {
          // Fallback to list endpoint with filters
          const filterParams = {
            section: section,
            statut: 'actif',
            limit: 100,
            page: 1,
          };
          
          if (newFilters.annee) {
            filterParams.year = newFilters.annee;
          }
          if (newFilters.mois) {
            filterParams.month = newFilters.mois;
          }
          
          const videoId = _videoId || _video?.id || _video?._id;
          const fullVideo = await fetchVideoById(videoId, filterParams);
          setSelectedVideo(fullVideo);
          setVideoLoading(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } catch (listError) {
          // Fallback to provided video data
          setSelectedVideo(_video);
          setVideoLoading(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
    }

    const updatedFilters = { ...filters, ...filterOnly };

    // If jour (day) is specified, keep dateFrom/dateTo for week range context
    // but the API will filter by the specific day
    if (filterOnly.jour) {
      // Day selected - keep the week's dateFrom/dateTo but add jour parameter
      updatedFilters.jour = filterOnly.jour;
      updatedFilters.annee = filterOnly.annee || '';
      updatedFilters.mois = filterOnly.mois || '';
      updatedFilters.semaine = '';
    } else if (filterOnly.dateFrom || filterOnly.dateTo) {
      updatedFilters.annee = '';
      updatedFilters.mois = '';
      updatedFilters.semaine = '';
      updatedFilters.jour = '';
      updatedFilters.dateFrom = filterOnly.dateFrom || '';
      updatedFilters.dateTo = filterOnly.dateTo || '';
    } else {
      updatedFilters.dateFrom = '';
      updatedFilters.dateTo = '';
      updatedFilters.jour = '';
    }

    if (filterOnly.annee || filterOnly.mois || filterOnly.theme || filterOnly.semaine) {
      updatedFilters.dateFrom = '';
      updatedFilters.dateTo = '';
      updatedFilters.jour = '';
    }

    filterKeyRef.current = '';
    setFilters(updatedFilters);
    setAppliedFilters(updatedFilters);
    setFilterRunId(prev => prev + 1);
    setAllVideos([]);
    setPage(1);
    setShowGrid(true);
    setSelectedVideo(null);
  };

  const fetchThemeData = useCallback(async (theme) => {
    try {
      const res = await getVideoFilterTreeByCategory(theme);
      const data = res.data?.data || { years: [] };
      return data;
    } catch (err) {
      return { years: [] };
    }
  }, []);

  const videoTitle = currentVideo
    ? videoTitlePrefix
      ? videoTitlePrefix + (fmtDate(currentVideo.date).charAt(0).toUpperCase() + fmtDate(currentVideo.date).slice(1))
      : fmtDate(currentVideo.date).charAt(0).toUpperCase() + fmtDate(currentVideo.date).slice(1)
    : '';

  const availableYears = filterTree?.years || [];
  const selectedYearData = availableYears.find(y => String(y.year) === String(filters.annee));
  // const availableMonths = selectedYearData?.months || [];
  // const availableThemes = filterTree?.themes || [];

  /*
  const FilterFields = () => (
    <>
      <div className="filter-field">
        <label>Type de recherche:</label>
        <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}>
          <option value="">Choisir un type de recherche</option>
          {SEARCH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="filter-field">
        <label>Année</label>
        <select value={filters.annee} onChange={e => setFilters(f => ({ ...f, annee: e.target.value, mois: '', semaine: '' }))}>
          <option value="">Choisir une année</option>
          {availableYears.map(y => <option key={y.year} value={y.year}>{y.year}</option>)}
        </select>
      </div>
      <div className="filter-field">
        <label>Mois</label>
        <select
          value={filters.mois}
          onChange={e => setFilters(f => ({ ...f, mois: e.target.value, semaine: '' }))}
          disabled={!filters.annee}
        >
          <option value="">Choisir un mois</option>
          {availableMonths.map(m => <option key={m.month} value={m.month}>{m.monthName}</option>)}
        </select>
      </div>
      <div className="filter-field">
        <label>Semaine</label>
        <select
          value={filters.semaine}
          onChange={e => setFilters(f => ({ ...f, semaine: e.target.value }))}
          disabled={!filters.mois}
        >
          <option value="">Choisir la semaine</option>
          {((filters.annee && filters.mois)
            ? getWeeksForMonth(parseInt(filters.annee, 10), parseInt(filters.mois, 10))
            : []
          ).map(w => (
            <option key={w} value={w}>Semaine {w}</option>
          ))}
        </select>
      </div>
      {showThemeFilter && (
        <div className="filter-field">
          <label>Thème</label>
          <select value={filters.theme} onChange={e => setFilters(f => ({ ...f, theme: e.target.value }))}>
            <option value="">Choisir un thème</option>
            {availableThemes.map((t, idx) => <option key={idx} value={t}>{t}</option>)}
          </select>
        </div>
      )}
    </>
  );
  */

  return (
    <div className="two-col">
      <VideoTreeSidebar
        sections={sections}
        filterTree={filterTree}
        onFilterSelect={handleSidebarFilter}
        onFetchThemeData={fetchThemeData}
        onClearFilters={handleClear}
        hasActiveFilters={hasActiveFilters}
        filters={filters}
        selectedVideo={selectedVideo}
        multipleVideosPerDay={multipleVideosPerDay}
        isDayBased={isDayBased}
        className="video-sidebar"
      />

      <div className="main-col pt-3">
        {/* <div className="filter-bar desktop-only">
          <FilterFields />
          <div style={{ display: 'flex', gap: '8px' }}>
            {hasActiveFilters && (
              <button className="btn-mcm" style={{ backgroundColor: '#eee', color: '#333' }} onClick={handleClear}>EFFACER</button>
            )}
            <button className="btn-mcm" onClick={handleSearch}>RECHERCHER</button>
          </div>
        </div> */}

        {/* Mobile filter row – disabled (filter bar removed)
        <div className="mobile-filter-row">
          <button className="mobile-filter-btn" onClick={() => setFilterOpen(true)}>
            <FiSliders size={16} /> Filtre
          </button>
        </div>

        {filterOpen && (
          <div className="filter-modal-overlay" onClick={() => setFilterOpen(false)}>
            <div className="filter-modal" onClick={e => e.stopPropagation()}>
              <div className="filter-modal-header">
                <span>Filtre</span>
                <button onClick={() => setFilterOpen(false)}><FiX size={18} /></button>
              </div>
              <div className="filter-modal-body">
                <FilterFields />
              </div>
              <div style={{ display: 'flex', gap: '8px', padding: '16px' }}>
                <button className="btn-mcm filter-modal-submit" style={{ flex: 1, margin: 0 }} onClick={handleSearch}>RECHERCHER</button>
                {hasActiveFilters && (
                  <button className="btn-mcm filter-modal-submit" style={{ flex: 1, margin: 0, backgroundColor: '#eee', color: '#333' }} onClick={handleClear}>EFFACER</button>
                )}
              </div>
            </div>
          </div>
        )}
        */}

        {(treeLoading || (loading && allVideos.length === 0 && !selectedVideo)) ? (
          <div style={{ paddingTop: 8 }}>
            <SkeletonBox width="55%" height={18} borderRadius={4} style={{ marginBottom: 12 }} />
            <SkeletonBox width="30%" height={13} borderRadius={4} style={{ marginBottom: 20 }} />
            <SkeletonBox height={340} borderRadius={12} style={{ marginBottom: 16 }} />
            <SkeletonBox width="70%" height={13} borderRadius={4} style={{ marginBottom: 8 }} />
            <SkeletonBox width="45%" height={13} borderRadius={4} />
          </div>
        ) : showGrid ? (
          !loading && allVideos.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
              <FiVideo size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
              <p>Aucune vidéo trouvée pour ces critères.</p>
            </div>
          ) : (
            <>
              {allVideos.length >= 1 && !showAllVideos && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
                  <div style={{ maxWidth: '600px', width: '100%' }}>
                    {(() => {
                      const v = allVideos[0];
                      const title = videoTitlePrefix
                        ? videoTitlePrefix + (fmtDate(v.date).charAt(0).toUpperCase() + fmtDate(v.date).slice(1))
                        : fmtDate(v.date).charAt(0).toUpperCase() + fmtDate(v.date).slice(1);
                      return (
                        <>
                          {v.theme && <p style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', color: '#222' }}>{v.theme}</p>}
                          {title && <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>{title}</p>}
                          <VideoPreview video={v} />
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}



              {/* Remove "Afficher tout" button - only previews shown */}
              {false && !showAllVideos && allVideos.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                  <button
                    className="btn-mcm outline"
                    onClick={() => setShowAllVideos(true)}
                  >
                    Afficher tout
                  </button>
                </div>
              )}
              {false && showAllVideos && page < (paginationData.totalPages || 1) && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                  <button
                    className="btn-mcm outline"
                    disabled={loading}
                    onClick={() => setPage(p => p + 1)}
                  >
                    {loading ? 'Chargement…' : 'Charger plus'}
                  </button>
                </div>
              )}
            </>
          )
        ) : currentVideo ? (
          <>
            {currentVideo.theme && (
              <p className="video-title" style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px', lineHeight: 1.3 }}>
                {currentVideo.theme}
              </p>
            )}
            {videoTitle && (
              <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>{videoTitle}</p>
            )}
            <VideoPreview video={currentVideo} loading={videoLoading} />
          </>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <FiVideo size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p>Sélectionnez une vidéo pour la visionner.</p>
          </div>
        )}
      </div>

      {/* FAB disabled – filter bar removed
      <button
        className="semence-fab mobile-only"
        onClick={() => setShowGrid(g => !g)}
        aria-label="Toggle grid"
      >
        <FiGrid size={20} />
      </button>
      */}
    </div>
  );
}

